import {
  type IncomeComponents,
  IncomeComponentsSchema,
  type IncomeTestKind,
  type IncomeTestsInput,
  type IncomeTestsOutput,
  type IncomeTestsRuleSet,
  grossedUpRfba,
  netInvestmentLoss,
} from "./types.js";

/**
 * Adjusted fringe benefits total used by the FAMILY-ASSISTANCE (CCS/FTB) ATI.
 *
 * DSS FA Guide 3.2.3 (from 1 Jan 2017):
 *   adjusted fringe benefits total
 *     = exempt-employer RFBA × (1 − applicable FBT rate)
 *     + non-exempt-employer RFBA
 *
 * BUILD-CRITICAL: the (1 − FBT) un-gross-up applies ONLY to the s57A exempt-
 * employer portion (factor 0.53 for FBT years to 31 Mar 2027 — NOT 0.535, and
 * NOT a blanket multiplier over the whole RFBA). A non-exempt (ordinary novated
 * lease) benefit is added at its FULL grossed-up value even for ATI.
 */
export function adjustedFringeBenefitsTotal(
  c: IncomeComponents,
  rules: IncomeTestsRuleSet,
): number {
  const r = c.reportableFringeBenefits;
  return (
    r.exemptEmployer * rules.exemptEmployerAtiFactor + r.nonExemptEmployer
  );
}

/**
 * Income for surcharge purposes (MLS, private-health rebate, Div 293 base).
 * ATO: taxable income (incl. net FTDT) + total net investment loss + reportable
 * super + the FULL grossed-up RFBA (gross value used since 1 July 2017).
 */
export function incomeForSurchargePurposes(
  c: IncomeComponents,
): number {
  return (
    c.taxableIncome +
    c.familyTrustDistributionTax +
    netInvestmentLoss(c) +
    c.reportableSuperContributions +
    grossedUpRfba(c.reportableFringeBenefits)
  );
}

/**
 * HELP/STSL repayment income. ATO: taxable income + total net investment loss +
 * the FULL grossed-up RFBA + reportable super + exempt foreign employment income.
 */
export function helpRepaymentIncome(c: IncomeComponents): number {
  return (
    c.taxableIncome +
    netInvestmentLoss(c) +
    grossedUpRfba(c.reportableFringeBenefits) +
    c.reportableSuperContributions +
    c.exemptForeignEmploymentIncome
  );
}

/**
 * Division 293 income. Same surcharge base (taxable income + net investment loss
 * + reportable super + full grossed-up RFBA + net FTDT) but the taxed elements
 * of a super lump sum / FHSS released amount above the cap are SUBTRACTED
 * (sign matters — Div293 strips these out so they aren't double-counted).
 */
export function div293Income(c: IncomeComponents): number {
  return (
    incomeForSurchargePurposes(c) - c.superLumpSumTaxedElementsAboveCap
  );
}

/**
 * Adjusted taxable income (ATI) for family assistance (CCS/FTB).
 * Services Australia / DSS: taxable income + adjusted fringe benefits total
 * (the un-grossed-up RFBA, exempt-only) + reportable super + total net
 * investment loss + tax-free pensions/benefits + target foreign income
 * − child maintenance expenditure.
 *
 * Note: ATI uses the ADJUSTED fringe benefits total, NOT the raw grossed-up
 * RFBA the surcharge/HELP/Div293 derivations use. That asymmetry is the moat.
 */
export function adjustedTaxableIncome(
  c: IncomeComponents,
  rules: IncomeTestsRuleSet,
): number {
  return (
    c.taxableIncome +
    adjustedFringeBenefitsTotal(c, rules) +
    c.reportableSuperContributions +
    netInvestmentLoss(c) +
    c.taxFreePensionsBenefits +
    c.targetForeignIncome -
    c.childMaintenanceExpenditure
  );
}

/** Derive a single named income definition from the components. */
export function deriveIncome(
  kind: IncomeTestKind,
  c: IncomeComponents,
  rules: IncomeTestsRuleSet,
): number {
  switch (kind) {
    case "surcharge":
      return incomeForSurchargePurposes(c);
    case "help":
      return helpRepaymentIncome(c);
    case "div293":
      return div293Income(c);
    case "ati":
      return adjustedTaxableIncome(c, rules);
  }
}

/**
 * Derive EVERY income definition from one component set. Pure: same components +
 * ruleset always yield the same output. Validates at the boundary so malformed
 * payloads fail loudly rather than producing junk.
 */
export function deriveIncomeTests(
  rawInput: IncomeTestsInput,
  rules: IncomeTestsRuleSet,
): IncomeTestsOutput {
  const c = IncomeComponentsSchema.parse(rawInput.components);
  return {
    incomeForSurchargePurposes: incomeForSurchargePurposes(c),
    helpRepaymentIncome: helpRepaymentIncome(c),
    div293Income: div293Income(c),
    adjustedTaxableIncome: adjustedTaxableIncome(c, rules),
    adjustedFringeBenefitsTotal: adjustedFringeBenefitsTotal(c, rules),
    grossedUpFringeBenefits: grossedUpRfba(c.reportableFringeBenefits),
  };
}
